import React, { useMemo, useRef } from 'react';
import JSON5 from 'json5';
import AnalyticsChart from '../AnalyticsChart/AnalyticsChart.component';
import TableView, { TableData } from 'components/Reports/TableView';
import TableShimmer from './TableShimmer';
import ChartShimmer from './ChartShimmer';
import { projectChartData, detectSeriesDivergence, applyDualAxis } from 'helpers/ChartDataProjectionHelper';
import { processTableWithPatternDetection } from 'helpers/TableDataProjectionHelper';
import { sanitizeTableTrustPolicy } from 'helpers/tableTrustPolicy';
import { HIDE_UNVERIFIED_BY_DEFAULT } from 'helpers/tablePolicy';
import { analyticsRegex, tableRegex, sourcesRegex } from './ChatContentView.constants';
import './ChatContentView.scss';

/**
 * Fix objects having values without keys (e.g. { "val1", "val2" } becomes { "key1": "val1", "key2": "val2" })
 * This prevents the parser from crashing on malformed LLM output
 */
const fixMissingKeys = (jsonString: string): string => {
  let fixed = "";
  let lastIndex = 0;
  let match;
  // Regex matches strings and structural characters
  const re = /("(?:\\[\s\S]|[^"])*"|:|{|}|\[|\]|,)/g; 
  const stack: string[] = [];
  let expectingKey = false;
  let fixCount = 1;

  while ((match = re.exec(jsonString)) !== null) {
    const token = match[0];
    // Add skipped content (whitespace)
    fixed += jsonString.substring(lastIndex, match.index);
    lastIndex = re.lastIndex;

    if (token === '{') {
      stack.push('{');
      expectingKey = true;
      fixed += token;
    } else if (token === '[') {
      stack.push('[');
      fixed += token;
    } else if (token === '}' || token === ']') {
      stack.pop();
      expectingKey = false;
      fixed += token;
    } else if (token === ',') {
      fixed += token;
      if (stack[stack.length - 1] === '{') expectingKey = true;
    } else if (token === ':') {
      fixed += token;
      expectingKey = false;
    } else if (token.startsWith('"')) {
      if (stack[stack.length - 1] === '{' && expectingKey) {
        // Check if next structural token is colon
        const next = jsonString.substring(re.lastIndex).match(/^\s*([:},])/);
        if (!next || next[1] !== ':') {
          // It's an orphan value, add generic key to make it valid JSON
          fixed += `"missing_key_${fixCount++}": ${token}`;
          expectingKey = false; 
        } else {
          fixed += token;
        }
      } else {
        fixed += token;
      }
    } else {
      fixed += token;
    }
  }
  fixed += jsonString.substring(lastIndex);
  return fixed;
};

/**
 * Sanitize JSON string by fixing common LLM errors
 * This makes the JSON valid for parsing
 */
const sanitizeJson = (jsonString: string): string => {
  let sanitized = jsonString.trim();
  
    // Remove function expressions: key: function(params) { ... }
    sanitized = sanitized.replace(
      /(?:"[^"]+"|[\w$]+)\s*:\s*function\s*\([^)]*\)\s*\{[\s\S]*?\n\s*\}/g,
      '"formatter": "{b}"'
    );
  
  // Replace ${value} or $(value) with ECharts placeholders
  // {value} is used for axis labels, {c} for series data labels.
  sanitized = sanitized.replace(/\$[\({]value[\)}]/g, '{value}');
  
  // Fix missing quotes around string values that span until the next comma or closing brace
  // Pattern: "key": unquoted text that continues until , or } or ]
  // This handles long sentences with spaces, punctuation, etc.
  sanitized = sanitized.replace(
    /"([^"]+)"\s*:\s*([A-Za-z][^,}\]]*?)(\s*[,}\]])/g,
    (match, key, value, ending) => {
      const trimmedValue = value.trim();
      // Skip if value looks like a number, boolean, null, array, object, or already quoted
      if (/^-?\d/.test(trimmedValue) || 
          /^(true|false|null)$/i.test(trimmedValue) ||
          /^["'\[{]/.test(trimmedValue)) {
        return match;
      }
      // Escape any quotes within the value
      const escapedValue = trimmedValue.replace(/"/g, '\\"');
      return `"${key}": "${escapedValue}"${ending}`;
    }
  );
  
  // Fix orphaned values (missing keys)
  sanitized = fixMissingKeys(sanitized);

  // Remove trailing commas before closing braces/brackets
  sanitized = sanitized.replace(/,(\s*[}\]])/g, '$1');

  // Fix stray characters between comma and quote (e.g. ["Item", t "Value"]) which breaks parsing
  sanitized = sanitized.replace(/,\s*[a-zA-Z]\s*"/g, ', "');
  
  // Find the outermost JSON object boundaries
  // This removes any extra text after the JSON object
  const firstBrace = sanitized.indexOf('{');
  if (firstBrace === -1) return sanitized;
  
  let braceCount = 0;
  let lastBrace = -1;
  
  for (let i = firstBrace; i < sanitized.length; i++) {
    if (sanitized[i] === '{') braceCount++;
    else if (sanitized[i] === '}') {
      braceCount--;
      if (braceCount === 0) {
        lastBrace = i;
        break;
      }
    }
  }
  
  if (lastBrace !== -1) {
    sanitized = sanitized.substring(firstBrace, lastBrace + 1);
  }
  
  return sanitized;
};

/**
 * Convert Vega-Lite chart spec to Apache ECharts format.
 * LLMs sometimes produce Vega-Lite when the data is flat/tabular (e.g. [{Year, Value, Source}]).
 * This pivots those records into ECharts series arrays so the chart renders correctly.
 */
const convertVegaLiteToECharts = (obj: any): any => {
  if (!obj.mark && !obj.encoding) return obj; // Already ECharts or unknown ÔÇö leave untouched
  const values: any[] = obj.data?.values || [];
  const xField = obj.encoding?.x?.field;
  const yField = obj.encoding?.y?.field;
  const colorField = obj.encoding?.color?.field;
  if (!xField || !yField) return obj;
  const xValues = Array.from(new Set(values.map((v: any) => String(v[xField])))).sort();
  const groups = new Map<string, Map<string, number>>();
  values.forEach((v: any) => {
    const name = colorField ? String(v[colorField]) : yField;
    if (!groups.has(name)) groups.set(name, new Map());
    groups.get(name)!.set(String(v[xField]), v[yField]);
  });
  return {
    title: { text: obj.title || '' },
    tooltip: { trigger: 'axis' },
    legend: { data: Array.from(groups.keys()) },
    xAxis: { type: 'category', data: xValues },
    yAxis: { type: 'value' },
    series: Array.from(groups.entries()).map(([name, dataMap]) => ({
      name, type: 'line', data: xValues.map((x: string) => dataMap.get(x) ?? null),
    })),
  };
};

/**
 * Convert Chart.js chart spec to Apache ECharts format.
 * LLMs sometimes produce Chart.js format ({ chart, data.labels, data.datasets }) despite instructions.
 * Data is already pivoted, so this is a straightforward field mapping.
 */
const convertChartJsToECharts = (obj: any): any => {
  if (!obj.chart || !obj.data?.labels || !Array.isArray(obj.data?.datasets)) return obj; // Not Chart.js ÔÇö leave untouched
  const labels: string[] = obj.data.labels.map(String);
  const type = obj.chart === 'bar' ? 'bar' : 'line';
  return {
    title: { text: obj.options?.plugins?.title?.text || '' },
    tooltip: { trigger: 'axis' },
    legend: { data: obj.data.datasets.map((ds: any) => ds.label || '') },
    xAxis: { type: 'category', data: labels, name: obj.options?.scales?.x?.title?.text || '' },
    yAxis: { type: 'value', name: obj.options?.scales?.y?.title?.text || '' },
    series: obj.data.datasets.map((ds: any) => ({
      name: ds.label || '',
      type,
      data: ds.data,
      smooth: type === 'line',
    })),
  };
};

/**
 * Sanitize HTML string by fixing malformed/unclosed tags
 * Uses browser's DOMParser which automatically corrects malformed HTML
 */
const sanitizeHtml = (htmlString: string): string => {
  if (!htmlString || typeof window === 'undefined') return htmlString;
  
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    // Remove empty chart containers and script tags left by LLM-generated Chart.js code
    doc.body.querySelectorAll('script, .chart-container, canvas').forEach(el => el.remove());
    return doc.body.innerHTML;
  } catch (e) {
    // If parsing fails, return original string
    return htmlString;
  }
};

const createStableContentKey = (prefix: string, index: number, content: string): string => {
  let hash = 0;

  for (let i = 0; i < content.length; i++) {
    hash = ((hash << 5) - hash) + content.charCodeAt(i);
    hash |= 0;
  }

  return `${prefix}-${index}-${Math.abs(hash)}`;
};

export type ChatContentViewProps = {
  content: string;
  className?: string;
  onClick?: (event: React.MouseEvent) => void;
};

/**
 * ChatContentView component with streaming optimization
 * 
 * Features:
 * 1. Prevents chart/table blinking during streaming by caching stable components
 * 2. Shows shimmer loading state for incomplete JSON blocks
 * 3. Uses position-based keys for simplicity and performance
 * 
 * Streaming behavior:
 * - Incomplete JSON ÔåÆ Shows shimmer skeleton
 * - Complete JSON ÔåÆ Renders actual chart/table (cached)
 * - Once rendered, component stays stable even as more content streams
 */
const ChatContentView: React.FC<ChatContentViewProps> = ({ content, className = '', onClick }) => {
  // Cache rendered components by position to prevent re-rendering during streaming
  const stableComponentsRef = useRef<Map<string, React.ReactElement>>(new Map());
  
  const parsedContent = useMemo(() => {
    if (!content) return { htmlContent: '', components: [], incompleteBlocks: [] };

    // Normalize markdown-fenced JSON blocks to our __ANALYZE_JSON_START__ delimiter.
    // LLMs occasionally wrap chart JSON in ```json ``` or ```html ``` instead of using the required delimiter.
    const normalizedContent = content.replace(
      /```(?:json|html)?\s*(\{[\s\S]*?\})\s*```/g,
      (_, json) => (json.includes('"series"') || json.includes('"mark"') || json.includes('"encoding"') || json.includes('"datasets"'))
        ? `__ANALYZE_JSON_START__${json}__ANALYZE_JSON_END__`
        : `\`\`\`json\n${json}\n\`\`\``
    );
    let htmlContent = normalizedContent
      .replace(/```\w*\s*```/g, '')  // Remove empty fenced code blocks
      .replace(/```html\s*([\s\S]*?)```/g, '$1');  // Strip ```html fences, keep inner HTML
    const components: React.ReactElement[] = [];
    const incompleteBlocks: Array<{ type: string; placeholder: string }> = [];
    let componentIndex = 0;
    const analyticsMatches: RegExpExecArray[] = [];
    const tableMatches: RegExpExecArray[] = [];
    let analyticsMatch;
    
    // Reset regex lastIndex to ensure clean matches
    analyticsRegex.lastIndex = 0;
    tableRegex.lastIndex = 0;
    sourcesRegex.lastIndex = 0;
    
    // First, collect all complete matches
    while ((analyticsMatch = analyticsRegex.exec(normalizedContent)) !== null) {
      analyticsMatches.push(analyticsMatch);
    }

    let tableMatch;
    while ((tableMatch = tableRegex.exec(normalizedContent)) !== null) {
      tableMatches.push(tableMatch);
    }

    // Hide sources JSON from display
    let sourcesMatch;
    while ((sourcesMatch = sourcesRegex.exec(normalizedContent)) !== null) {
      htmlContent = htmlContent.replace(sourcesMatch[0], '');
    }

    // Process complete analytics components FIRST
    analyticsMatches.forEach((match, matchIndex) => {
      try {
        const jsonContent = sanitizeJson(match[1].trim());
        const analyticsData: any = convertChartJsToECharts(convertVegaLiteToECharts(JSON5.parse(jsonContent)));
        
        // Fix: Ensure series data is never null (ECharts requires array)
        if (analyticsData.series && Array.isArray(analyticsData.series)) {
          analyticsData.series.forEach((s: any) => {
            if (s.data === null) s.data = [];
          });
        }

        // Hide chart if it still lacks a renderable series array after all conversions
        const hasValidSeries = Array.isArray(analyticsData.series) && analyticsData.series.length > 0;
        if (!hasValidSeries) {
          console.warn('[ChatContentView] Chart hidden ÔÇö no valid series after parsing:', analyticsData);
          htmlContent = htmlContent.replace(match[0], '');
          return;
        }

        const placeholder = `__COMPONENT_${componentIndex}__`;
        
        // Use position-based key for stability (simpler than content hash)
        const key = createStableContentKey('analytics', componentIndex, jsonContent);
        // Create a unique chartId based on the full match for deletion
        const chartId = `analytics-chart-${matchIndex}`;
        const originalMatch = match[0];
        
        const handleDelete = (id: string) => {
          window.dispatchEvent(new CustomEvent('analyticsChartDeleted', { 
            detail: { chartId: id, originalMatch } 
          }));
        };
        
        // Cache component on first render, reuse on subsequent renders
        if (!stableComponentsRef.current.has(key)) {
          const { data: interpolatedAnalyticsData, isFilled } = projectChartData(analyticsData, true);
          const divergenceNote = detectSeriesDivergence(analyticsData);
          const chartData = applyDualAxis(interpolatedAnalyticsData);
          stableComponentsRef.current.set(
            key,
            <AnalyticsChart
              key={key}
              divergenceNote={divergenceNote}
              data={chartData}
              chartId={chartId}
              onDelete={handleDelete}
            />
          );
        }
        
        components.push(stableComponentsRef.current.get(key)!);
        htmlContent = htmlContent.replace(match[0], placeholder);
        componentIndex++;
      } catch (e) {
        console.warn('[ChatContentView] Chart JSON could not be rendered ÔÇö hiding from UI:', e);
        htmlContent = htmlContent.replace(match[0], '');
      }
    });

    tableMatches.forEach((match) => {
      try {
        const jsonContent = sanitizeJson(match[1].trim());
        const placeholder = `__COMPONENT_${componentIndex}__`;
        const key = createStableContentKey('table', componentIndex, jsonContent);
        
        // Cache component on first render, reuse on subsequent renders
        if (!stableComponentsRef.current.has(key)) {
          const tableData: TableData = JSON5.parse(jsonContent);
          const sanitizedTableData = sanitizeTableTrustPolicy(tableData);
          const { data, isFilled } = processTableWithPatternDetection(sanitizedTableData);
          stableComponentsRef.current.set(
            key,
            <TableView 
              key={key}
              data={data} 
              sortable={true} 
              showExportButtons={true}
              exportFormats={['excel']}
              filename={'table-data'}
              isFilled={isFilled}
              hideUnverified={HIDE_UNVERIFIED_BY_DEFAULT}
            />
          );
        }
        
        components.push(stableComponentsRef.current.get(key)!);
        htmlContent = htmlContent.replace(match[0], placeholder);
        componentIndex++;
      } catch (e) {
        console.warn('[ChatContentView] Table JSON could not be rendered ÔÇö hiding from UI:', e);
        htmlContent = htmlContent.replace(match[0], '');
      }
    });

    // NOW detect incomplete JSON blocks (after processing complete ones)
    // This ensures we only show shimmer for truly incomplete blocks
    const incompleteAnalyticsMatch = htmlContent.match(/_{1,2}ANALYZE_JSON_START__([\s\S]*)$/);
    const incompleteTableMatch = htmlContent.match(/_{1,2}TABLE_JSON_START__([\s\S]*)$/);
    
    // Check if the incomplete match is truly incomplete (no END marker)
    if (incompleteAnalyticsMatch && !incompleteAnalyticsMatch[1].includes('__ANALYZE_JSON_END__')) {
      const placeholder = `__LOADING_ANALYTICS__`;
      htmlContent = htmlContent.replace(/_{1,2}ANALYZE_JSON_START__[\s\S]*$/, placeholder);
      incompleteBlocks.push({ type: 'analytics', placeholder });
    }
    
    if (incompleteTableMatch && !incompleteTableMatch[1].includes('__TABLE_JSON_END__')) {
      const placeholder = `__LOADING_TABLE__`;
      htmlContent = htmlContent.replace(/_{1,2}TABLE_JSON_START__[\s\S]*$/, placeholder);
      incompleteBlocks.push({ type: 'table', placeholder });
    }

    return { htmlContent, components, incompleteBlocks };
  }, [content]);

  const renderContent = useMemo(() => {
    const { htmlContent, components, incompleteBlocks } = parsedContent;
    
    if (components.length === 0 && incompleteBlocks.length === 0) {
      return (
        <div 
          className="chat-content__html"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlContent) }} 
        />
      );
    }

    // Split by both component and loading placeholders
    const parts = htmlContent.split(/(__(?:COMPONENT_\d+|LOADING_(?:ANALYTICS|TABLE))__)/);
    const result: (string | React.ReactElement)[] = [];

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      
      // Check if it's a placeholder
      const componentMatch = part.match(/__COMPONENT_(\d+)__/);
      const loadingMatch = part.match(/__LOADING_(ANALYTICS|TABLE)__/);
      
      if (componentMatch) {
        // Render actual component
        const componentIndex = parseInt(componentMatch[1]);
        if (components[componentIndex]) {
          result.push(components[componentIndex]);
        }
      } else if (loadingMatch) {
        // Render shimmer/loading state
        const loadingType = loadingMatch[1].toLowerCase();
        result.push(
          <div key={`loading-${loadingType}-${i}`} className="chat-content__loading">
            {loadingType === 'table' ? (
              <TableShimmer rows={5} columns={4} />
            ) : (
              <ChartShimmer bars={7} height={280} />
            )}
          </div>
        );
      } else if (part.trim()) {
        // Render HTML content
        result.push(
          <div 
            key={`html-${i}`}
            className="chat-content__html"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(part) }} 
          />
        );
      }
    }

    return result;
  }, [parsedContent]);

  return (
    <div className={`chat-content ${className}`} data-testid="chat-content-view" onClick={onClick}>
      {renderContent}
    </div>
  );
};

export default ChatContentView;
